import { divInt, sub, toPixels } from "../shared/fixed";
import type { MatchRules } from "./ability";
import type { SimEvent } from "./events";
import { PRESS_BUFFER } from "./command";
import { FIGHTER, PRESS_NEVER, type FighterState, type MatchState } from "./state";
import { setState } from "./transition";

/**
 * Захват на L1 вплотную (Джон 04.11.2026, 23:25–23:26: «Не хватает также захватов», «Вперёд + L1»): бросок через плечо
 * по паре клипов Mixamo Shoulder Throw (Aggressor и Victim, сняты вместе). Соперник перелетает через бросающего и падает
 * у него за спиной — стороны меняются. Вырваться — L1 в первые TECH_FRAMES кадров. Блок захват не держит.
 *
 * Путь из пары клипов (замер tools: кадры 110–230 при 60 к/с, шаг 10): где бёдра соперника и бросающего относительно
 * точки начала броска (вперёд по взгляду бросающего +), пиксели мира. Кадр мира t ↔ кадр клипа 110 + 1,5 t. Оба пути —
 * от одной точки: иначе на ударе о пол соперник в мире и в клипе расходились на 70 px (консультант 05.11.2026).
 */
const VICTIM_FRONT = [83, 96, 103, 98, 84, 61, 31, 4, -14, -33, -42, -41, -40] as const;
/** Где соперник встаёт в начале броска: впереди бросающего (render ставит его модель туда же). */
export const VICTIM_START = VICTIM_FRONT[0];
const ATTACKER_FORWARD = [0, 31, 47, 51, 46, 41, 42, 48, 59, 69, 72, 71, 68] as const;
/** Кадров мира на шаг таблицы (10 кадров клипа при 60 к/с, клип идёт в 1,5 раза быстрее мира). */
const STEP = 20;
const STEP_DIVISOR = 3;

export const THROW_FRAMES = 80;
/** Кадр удара о пол: урон, пыль, соперник лежит. */
export const THROW_SLAM = 60;
export const TECH_FRAMES = 10;
/** Вырвался: оба пошатнулись столько кадров и разлетелись с такой скоростью, пиксели за кадр. */
const TECH_STAGGER = 12;
const TECH_PUSH = 11;
const THROW_DAMAGE = 110;
/** Дальше этого захват не достаёт (бойцы сходятся не ближе ширины, 76). */
export const GRAB_RANGE = 100;

const GRABBABLE = new Set<number>([
  FIGHTER.idle,
  FIGHTER.walkForward,
  FIGHTER.walkBack,
  FIGHTER.crouch,
  FIGHTER.block,
  FIGHTER.crouchBlock,
  FIGHTER.blockstun,
  FIGHTER.turn,
  FIGHTER.ability,
]);

/** Значение таблицы на кадре мира t (целые: линейно между шагами). */
function sample(table: readonly number[], t: number): number {
  const scaled = t * STEP_DIVISOR;
  const index = Math.min(table.length - 1, Math.floor(scaled / STEP));
  const next = Math.min(table.length - 1, index + 1);
  const rest = scaled - index * STEP;
  const from = table[index] ?? 0;
  const to = table[next] ?? from;
  return from + divInt((to - from) * rest, STEP);
}

/** Можно ли схватить: соперник на земле, рядом, не лежит, не в воздухе, не в чужом броске. */
export function canGrab(attacker: FighterState, defender: FighterState): boolean {
  if (defender.y < 0 || defender.vy < 0 || !GRABBABLE.has(defender.state) || defender.invisible > 0) return false;
  return Math.abs(defender.x - attacker.x) <= sub(GRAB_RANGE);
}

/** Начать бросок: бросающий на месте пары, соперник у него впереди, как в первом кадре клипа. */
export function startThrow(attacker: FighterState, defender: FighterState): void {
  attacker.facing = defender.x >= attacker.x ? 1 : -1;
  attacker.throwX = attacker.x;
  setState(defender, FIGHTER.thrown);
  defender.ability = -1;
  defender.vx = 0;
  defender.vy = 0;
  defender.facing = -attacker.facing;
  defender.x = attacker.x + sub(VICTIM_FRONT[0]) * attacker.facing;
}

/** Кадр броска: пара идёт по пути клипов; вырвался — разошлись; удар о пол — урон, соперник лежит за спиной. */
export function updateThrow(state: MatchState, rules: MatchRules, events: SimEvent[]): void {
  for (const side of [0, 1] as const) {
    const victim = state.fighters[side];
    if (victim.state !== FIGHTER.thrown) continue;
    const attackerSide: 0 | 1 = side === 0 ? 1 : 0;
    const attacker = state.fighters[attackerSide];
    const ability = attacker.state === FIGHTER.ability ? rules.kits[attackerSide][attacker.ability] : undefined;
    if (!ability?.input.grab) {
      setState(victim, FIGHTER.idle);
      continue;
    }
    const t = victim.stateFrame;
    // вырвался: L1 в первые кадры захвата — оба отскакивают, без урона
    if (t <= TECH_FRAMES && (victim.triggerAge[3] ?? PRESS_NEVER) <= PRESS_BUFFER) {
      victim.triggerAge[3] = PRESS_NEVER;
      // вырвался: оба отскакивают на шаг, коротко пошатнувшись (консультант: «стоят вплотную рука в руке»)
      attacker.ability = -1;
      for (const fighter of [victim, attacker]) {
        setState(fighter, FIGHTER.hitstun);
        fighter.stun = TECH_STAGGER;
      }
      victim.vx = sub(TECH_PUSH) * attacker.facing;
      attacker.vx = -sub(TECH_PUSH) * attacker.facing;
      events.push({
        kind: "block",
        frame: state.frame,
        who: attackerSide,
        x: toPixels((victim.x + attacker.x) / 2),
        y: -110,
        strength: 1,
        fx: "block/spark",
      });
      continue;
    }
    const forward = attacker.facing;
    // у стены путь пары не выносит бойцов за край арены (бой ботов 05.11.2026: брошенный оказывался на 4 px за краем)
    const inside = (x: number): number => Math.min(sub(rules.arena.right), Math.max(sub(rules.arena.left), x));
    attacker.x = inside(attacker.throwX + sub(sample(ATTACKER_FORWARD, t)) * forward);
    victim.x = inside(attacker.throwX + sub(sample(VICTIM_FRONT, t)) * forward);
    if (t === THROW_SLAM) {
      victim.health = Math.max(0, victim.health - THROW_DAMAGE);
      victim.juggle = 0;
      events.push({
        kind: "hit",
        frame: state.frame,
        who: attackerSide,
        x: toPixels(victim.x),
        y: -20,
        strength: 2,
        ability: ability.id,
        fx: "heavy/spark",
        sound: "hit-heavy",
        point: true,
      });
      events.push({ kind: "land", frame: state.frame, who: side, x: toPixels(victim.x), y: 0, strength: 2 });
    }
    // клип доигран лёжа — дальше подъём в бой из той же позы, а не скачком из полёта
    if (t >= THROW_FRAMES - 1) {
      setState(victim, victim.health === 0 ? FIGHTER.ko : FIGHTER.knockdown);
      victim.facing = forward;
      victim.fallKind = 0;
    }
  }
}
